import { useState } from 'react';
import { Mail, ChevronDown } from 'lucide-react';
import { useApp } from '../context/AppContext';
import EmailServiceSelector from './EmailServiceSelector';

interface EmailRedirectButtonProps {
    userEmail: string;
    quoteId: number;
    quoteDate: string; // YYYY-MM-DD
}

function buildDirectUrl(provider: 'gmail' | 'outlook' | 'yahoo' | 'proton', query: string): string {
    const q = encodeURIComponent(query);
    switch (provider) {
        case 'gmail': return `https://mail.google.com/mail/u/0/#search/${q.replace(/%20/g, '+')}`;
        case 'outlook': return `https://outlook.live.com/mail/0/inbox?search=${q}`;
        case 'yahoo': return `https://mail.yahoo.com/d/search/keyword=${q}`;
        case 'proton': return `https://mail.proton.me/#search?keyword=${q}`;
    }
}

function detectProvider(email: string): 'gmail' | 'outlook' | 'yahoo' | 'proton' | 'custom' {
    const domain = email.split('@')[1]?.toLowerCase() ?? '';
    if (domain === 'gmail.com') return 'gmail';
    if (['outlook.com', 'hotmail.com', 'live.com', 'msn.com'].includes(domain)) return 'outlook';
    if (['yahoo.com', 'yahoo.fr', 'ymail.com'].includes(domain)) return 'yahoo';
    if (['proton.me', 'protonmail.com', 'pm.me'].includes(domain)) return 'proton';
    return 'custom';
}

export default function EmailRedirectButton({ userEmail, quoteId, quoteDate }: EmailRedirectButtonProps) {
    const { lang } = useApp();
    const [showEmailSelector, setShowEmailSelector] = useState(false);

    const searchQuery = `lodiss devis ${quoteId}`;
    const provider = detectProvider(userEmail);
    const isCustom = provider === 'custom';

    const handleClick = () => {
        if (isCustom) {
            setShowEmailSelector(true);
            return;
        }
        window.open(buildDirectUrl(provider, searchQuery), '_blank', 'noopener,noreferrer');
    };

    const label = lang === 'fr' ? 'Voir dans ma boîte mail' : 'Open in mailbox';

    return (
        <>
            <button
                onClick={handleClick}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600
                   hover:border-lodiss-red hover:text-lodiss-red transition-colors bg-white select-none"
            >
                <Mail size={14} />
                <span>{label}</span>
                {isCustom && (
                    <ChevronDown size={13} className="ml-0.5" />
                )}
            </button>

            <EmailServiceSelector
                isOpen={showEmailSelector}
                onClose={() => setShowEmailSelector(false)}
                quoteId={quoteId}
                quoteDate={quoteDate}
            />
        </>
    );
}

